import { useState, useEffect } from "react"
import { User, Bell, Shield, Palette, AlertTriangle, Save, LogOut, Monitor, Moon, Sun, TrendingUp } from "lucide-react"
import { useClerk, useUser } from "@clerk/react"
import { useNavigate } from "react-router-dom"
import api from "../services/api"
import { EXCHANGES } from "../utils/constants"
import toast from "react-hot-toast"

const THEMES = [
  { id: "dark", label: "Dark", icon: Moon },
  { id: "light", label: "Light", icon: Sun },
  { id: "system", label: "System", icon: Monitor },
]

const NOTIFICATION_DEFAULTS = { trade_executed: true, bot_stopped: true, drawdown_alert: true, backtest_complete: true, signal_followed: false, weekly_report: true, marketing_emails: false }

export default function Settings() {
  const { user } = useUser()
  const { signOut } = useClerk()
  const navigate = useNavigate()
  const [profile, setProfile] = useState({ full_name: "", username: "", email: "" })
  const [prefs, setPrefs] = useState({ default_exchange: "", risk_level: "Medium", default_timeframe: "1h", paper_trading: true })
  const [notifs, setNotifs] = useState(NOTIFICATION_DEFAULTS)
  const [theme, setTheme] = useState(() => localStorage.getItem("app_theme") || "dark")
  const [saving, setSaving] = useState(false)
  const [savingNotifs, setSavingNotifs] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activeSection, setActiveSection] = useState("profile")

  useEffect(() => {
    if (user) setProfile({ full_name: user.fullName || "", username: user.username || "", email: user.primaryEmailAddress?.emailAddress || "" })
    api.get("/auth/me").then(r => {
      const d = r.data
      if (d.full_name) setProfile(p => ({ ...p, full_name: d.full_name, username: d.username || p.username }))
      if (d.preferences) setPrefs(pr => ({ ...pr, ...d.preferences }))
      if (d.notification_preferences) setNotifs(n => ({ ...n, ...d.notification_preferences }))
    }).catch(() => {})
  }, [user])

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove("theme-dark", "theme-light", "theme-system")
    root.classList.add(`theme-${theme}`)
    localStorage.setItem("app_theme", theme)
  }, [theme])

  async function saveProfile(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.patch("/auth/me", { full_name: profile.full_name, username: profile.username, preferences: prefs })
      toast.success("Profile saved!")
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save") }
    finally { setSaving(false) }
  }

  async function saveNotifications() {
    setSavingNotifs(true)
    try {
      await api.patch("/auth/me", { notification_preferences: notifs })
      toast.success("Notification preferences saved!")
    } catch { toast.error("Failed to save preferences") }
    finally { setSavingNotifs(false) }
  }

  async function handleSignOut() {
    await signOut()
    navigate("/login")
  }

  const SECTIONS = [
    { id: "profile", label: "Profile", icon: User },
    { id: "trading", label: "Trading Preferences", icon: TrendingUp },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "security", label: "Security", icon: Shield },
    { id: "appearance", label: "Appearance", icon: Palette },
  ]

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
      {/* Sidebar nav */}
      <div className="xl:col-span-1">
        <div className="card p-2">
          <div className="flex items-center gap-3 p-3 mb-2 border-b border-surface-200 pb-4">
            <div className="w-10 h-10 bg-brand-500/20 rounded-xl flex items-center justify-center text-brand-400 font-bold text-lg">
              {(user?.firstName || user?.username || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-white text-sm truncate">{user?.fullName || user?.username || "User"}</p>
              <p className="text-xs text-gray-400 truncate">{user?.primaryEmailAddress?.emailAddress}</p>
            </div>
          </div>
          {SECTIONS.map(s => {
            const Icon = s.icon
            return (
              <button key={s.id} onClick={() => setActiveSection(s.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${activeSection === s.id ? "bg-brand-500/10 text-brand-300" : "text-gray-400 hover:text-white hover:bg-surface-100"}`}>
                <Icon size={15}/>{s.label}
              </button>
            )
          })}
          <div className="border-t border-surface-200 mt-2 pt-2">
            <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-trading-red hover:bg-trading-red/10 transition-colors">
              <LogOut size={15}/> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="xl:col-span-3">
        {activeSection === "profile" && (
          <div className="card">
            <h3 className="font-semibold text-white mb-5">Profile Information</h3>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="label">Full Name</label><input className="input" value={profile.full_name} onChange={e => setProfile({...profile, full_name: e.target.value})} placeholder="John Doe"/></div>
                <div><label className="label">Username</label><input className="input" value={profile.username} onChange={e => setProfile({...profile, username: e.target.value})} placeholder="johndoe"/></div>
              </div>
              <div><label className="label">Email</label><input className="input opacity-60 cursor-not-allowed" value={profile.email} readOnly/><p className="text-xs text-gray-500 mt-1">Email is managed by your account provider</p></div>
              <div className="flex justify-end pt-2">
                <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2 text-sm"><Save size={14}/>{saving ? "Saving..." : "Save Changes"}</button>
              </div>
            </form>
          </div>
        )}

        {activeSection === "trading" && (
          <div className="card">
            <h3 className="font-semibold text-white mb-5">Trading Preferences</h3>
            <div className="space-y-4">
              <div>
                <label className="label">Default Exchange</label>
                <select className="input" value={prefs.default_exchange} onChange={e => setPrefs({...prefs, default_exchange: e.target.value})}>
                  <option value="">None selected</option>
                  {EXCHANGES.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Default Risk Level</label>
                <div className="grid grid-cols-3 gap-2">
                  {["Low","Medium","High"].map(r => (
                    <button key={r} onClick={() => setPrefs({...prefs, risk_level: r})} className={`py-2.5 rounded-xl text-sm font-medium border transition-all ${prefs.risk_level === r ? r === "High" ? "bg-trading-red/10 border-trading-red/40 text-trading-red" : r === "Medium" ? "bg-trading-yellow/10 border-trading-yellow/40 text-trading-yellow" : "bg-trading-green/10 border-trading-green/40 text-trading-green" : "border-surface-200 text-gray-400 hover:text-white hover:bg-surface-100"}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Default Timeframe</label>
                <select className="input" value={prefs.default_timeframe} onChange={e => setPrefs({...prefs, default_timeframe: e.target.value})}>
                  {["1m","5m","15m","30m","1h","4h","1d","1w"].map(tf => <option key={tf} value={tf}>{tf}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between p-4 bg-surface-100 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-white">Paper Trading Mode</p>
                  <p className="text-xs text-gray-400 mt-0.5">New bots start in paper trading by default</p>
                </div>
                <button onClick={() => setPrefs({...prefs, paper_trading: !prefs.paper_trading})} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${prefs.paper_trading ? "bg-brand-500" : "bg-surface-200"}`}>
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${prefs.paper_trading ? "translate-x-5" : "translate-x-1"}`}/>
                </button>
              </div>
              <div className="flex justify-end">
                <button onClick={saveProfile} disabled={saving} className="btn-primary flex items-center gap-2 text-sm"><Save size={14}/>{saving ? "Saving..." : "Save"}</button>
              </div>
            </div>
          </div>
        )}

        {activeSection === "notifications" && (
          <div className="card">
            <h3 className="font-semibold text-white mb-5">Notification Preferences</h3>
            <div className="space-y-3">
              {[
                ["trade_executed", "Trade Executed", "When a bot opens or closes a trade"],
                ["bot_stopped", "Bot Stopped", "When a bot stops due to an error or circuit breaker"],
                ["drawdown_alert", "Drawdown Alert", "When drawdown exceeds your threshold"],
                ["backtest_complete", "Backtest Complete", "When a backtest finishes running"],
                ["signal_followed", "Signal Followed", "When someone subscribes to your signal"],
                ["weekly_report", "Weekly Report", "Performance summary every Monday"],
                ["marketing_emails", "Marketing Emails", "Product updates and promotions"],
              ].map(([key, label, desc]) => (
                <div key={key} className="flex items-center justify-between p-4 bg-surface-100 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-white">{label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                  </div>
                  <button onClick={() => setNotifs(n => ({...n, [key]: !n[key]}))} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${notifs[key] ? "bg-brand-500" : "bg-surface-200"}`}>
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${notifs[key] ? "translate-x-5" : "translate-x-1"}`}/>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={saveNotifications} disabled={savingNotifs} className="btn-primary flex items-center gap-2 text-sm"><Save size={14}/>{savingNotifs ? "Saving..." : "Save Preferences"}</button>
            </div>
          </div>
        )}

        {activeSection === "security" && (
          <div className="space-y-4">
            <div className="card">
              <h3 className="font-semibold text-white mb-4">Security</h3>
              <div className="space-y-3">
                {[["Password", "Managed by your auth provider", "bg-trading-green/10 text-trading-green", "Secured"], ["Two-Factor Auth", "Add an extra layer of security", "bg-surface-100 text-gray-400", "Set Up"]].map(([k, d, c, action]) => (
                  <div key={k} className="flex items-center justify-between p-4 bg-surface-100 rounded-xl">
                    <div><p className="text-sm font-medium text-white">{k}</p><p className="text-xs text-gray-400 mt-0.5">{d}</p></div>
                    <span className={`text-xs font-medium px-3 py-1 rounded-lg ${c}`}>{action}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card border-trading-red/20">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle size={16} className="text-trading-red"/>
                <h3 className="font-semibold text-white">Danger Zone</h3>
              </div>
              <p className="text-sm text-gray-400 mb-4">These actions are permanent and cannot be undone.</p>
              <button onClick={() => setShowDeleteConfirm(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-trading-red border border-trading-red/20 hover:bg-trading-red/10 transition-colors">
                <AlertTriangle size={13}/> Delete Account
              </button>
            </div>
          </div>
        )}

        {activeSection === "appearance" && (
          <div className="card">
            <h3 className="font-semibold text-white mb-5">Appearance</h3>
            <div>
              <label className="label">Theme</label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {THEMES.map(t => {
                  const Icon = t.icon
                  return (
                    <button key={t.id} onClick={() => setTheme(t.id)} className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${theme === t.id ? "border-brand-500/60 bg-brand-500/5 text-brand-300" : "border-surface-200 text-gray-400 hover:text-white hover:bg-surface-100"}`}>
                      <Icon size={20}/>
                      <span className="text-sm">{t.label}</span>
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3">Theme preference is saved locally and applied immediately to the interface.</p>
            </div>
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-50 border border-trading-red/30 rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-trading-red/10 rounded-xl"><AlertTriangle size={20} className="text-trading-red"/></div>
              <h3 className="font-bold text-white">Delete Account?</h3>
            </div>
            <p className="text-sm text-gray-400 mb-6">All your bots, strategies, backtests and data will be permanently deleted. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={async () => { try { await api.delete("/auth/me"); await signOut(); navigate("/") } catch { toast.error("Failed to delete account") } setShowDeleteConfirm(false) }} className="flex-1 px-4 py-2.5 rounded-xl bg-trading-red/10 text-trading-red border border-trading-red/20 hover:bg-trading-red/20 text-sm font-medium">Delete Account</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
